/**
 * Hides quantity controls and remove buttons for free gift line items
 * inside the Recharge cart drawer open shadow root.
 */
(function () {
  const FREE_GIFT_PROPERTY_KEYS = ['_free_gift', '_rc_free_gift'];
  const RECHARGE_DRAWER_HOST_SELECTOR =
    '#recharge-storefront-experiences__cart-drawer, [id*="recharge-storefront-experiences"][id*="cart-drawer"]';
  const RECHARGE_ROOT_SELECTOR = '#recharge-storefront-experiences, [id^="recharge-storefront-experiences"]';
  const SHADOW_STYLE_ID = 'colapaw-free-gift-cart-styles';

  const SHADOW_STYLES = `
    [role="listitem"][data-free-gift-line-item="true"] .flex.items-center.rounded-storefront-drawer.border,
    [role="listitem"][data-free-gift-line-item="true"] input[name="quantity"],
    [role="listitem"][data-free-gift-line-item="true"] button[data-free-gift-remove="true"] {
      display: none !important;
    }
  `;

  let freeGiftItems = [];
  const observedNodes = new WeakSet();
  let refreshQueued = false;

  function normalizeText(value) {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  function getItemProperties(item) {
    if (Array.isArray(item?.properties)) {
      return Object.fromEntries(
        item.properties
          .filter((entry) => entry?.name)
          .map((entry) => [entry.name, entry.value])
      );
    }

    return item?.properties || {};
  }

  function isFreeGiftItem(item) {
    const properties = getItemProperties(item);

    if (
      FREE_GIFT_PROPERTY_KEYS.some((key) => {
        const value = properties[key];
        return value != null && value !== '' && value !== false;
      })
    ) {
      return true;
    }

    if (
      item.parent_relationship?.parent_key &&
      item.final_line_price === 0 &&
      !item.selling_plan_allocation
    ) {
      return true;
    }

    return false;
  }

  async function loadFreeGiftItems() {
    try {
      const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
      const response = await fetch(`${cartUrl}.js`, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return [];

      const cart = await response.json();
      freeGiftItems = (cart.items || []).filter(isFreeGiftItem);
      return freeGiftItems;
    } catch (error) {
      console.error('[cart-free-gifts-drawer] Failed to load cart items', error);
      return freeGiftItems;
    }
  }

  function getRemoveLabel(item) {
    return normalizeText(`Remove ${item.product_title}`);
  }

  function getProductTitle(item) {
    return normalizeText(item.product_title);
  }

  function getShadowRoot(node) {
    if (!node?.shadowRoot) return null;
    return node.shadowRoot;
  }

  function collectShadowRoots() {
    const roots = [];
    const seen = new Set();

    function addRoot(root) {
      if (!root || seen.has(root)) return;
      seen.add(root);
      roots.push(root);
    }

    document.querySelectorAll(RECHARGE_DRAWER_HOST_SELECTOR).forEach((host) => {
      addRoot(getShadowRoot(host));
    });

    const rechargeRoot = document.querySelector(RECHARGE_ROOT_SELECTOR);
    rechargeRoot?.querySelectorAll('*').forEach((element) => {
      addRoot(getShadowRoot(element));
    });

    return roots;
  }

  function ensureShadowStyles(shadowRoot) {
    if (!shadowRoot || shadowRoot.getElementById?.(SHADOW_STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = SHADOW_STYLE_ID;
    style.textContent = SHADOW_STYLES;
    shadowRoot.appendChild(style);
  }

  function getLineItemProductTitle(lineItem) {
    return normalizeText(lineItem.querySelector('p.font-semibold')?.textContent);
  }

  function getLineItemRemoveLabel(lineItem) {
    return normalizeText(lineItem.querySelector('button[type="button"] .sr-only')?.textContent);
  }

  function findMatchingFreeGift(lineItem) {
    const title = getLineItemProductTitle(lineItem);
    const removeLabel = getLineItemRemoveLabel(lineItem);
    const imageSrc = lineItem.querySelector('img')?.src || '';

    return freeGiftItems.find((item) => {
      const matchesTitle = title && getProductTitle(item) === title;
      const matchesRemove = removeLabel && getRemoveLabel(item) === removeLabel;
      const itemImage = item.image || item.featured_image?.url || '';
      const matchesImage =
        itemImage &&
        imageSrc &&
        (imageSrc.includes(encodeURI(itemImage.split('?')[0])) ||
          imageSrc.includes(String(item.variant_id)));

      return matchesTitle || matchesRemove || matchesImage;
    });
  }

  function markFreeGiftLineItem(lineItem, item) {
    lineItem.dataset.freeGiftLineItem = 'true';
    lineItem.dataset.freeGiftVariantId = String(item.variant_id);
    lineItem.classList.add('cart-line-item--free-gift');
  }

  function hideFreeGiftControls(lineItem) {
    lineItem.querySelectorAll('input[name="quantity"], input[aria-label="Quantity"]').forEach((input) => {
      input.hidden = true;
      input.setAttribute('aria-hidden', 'true');
      input.closest('.flex.items-center.rounded-storefront-drawer.border')?.setAttribute('hidden', 'hidden');
    });

    lineItem.querySelectorAll('button[type="button"]').forEach((button) => {
      const label = normalizeText(button.querySelector('.sr-only')?.textContent);
      if (!label.startsWith('remove ')) return;

      button.hidden = true;
      button.setAttribute('aria-hidden', 'true');
      button.dataset.freeGiftRemove = 'true';
    });
  }

  function applyFreeGiftUi(searchRoot) {
    if (!searchRoot) return;

    ensureShadowStyles(searchRoot);

    if (!freeGiftItems.length) return;

    searchRoot.querySelectorAll('[role="listitem"]').forEach((lineItem) => {
      const matchedItem = findMatchingFreeGift(lineItem);
      if (!matchedItem) return;

      markFreeGiftLineItem(lineItem, matchedItem);
      hideFreeGiftControls(lineItem);
    });
  }

  async function refreshRechargeDrawer() {
    await loadFreeGiftItems();

    collectShadowRoots().forEach((shadowRoot) => {
      applyFreeGiftUi(shadowRoot);
      observeNode(shadowRoot);
    });
  }

  function queueRefresh() {
    if (refreshQueued) return;
    refreshQueued = true;

    window.requestAnimationFrame(async () => {
      refreshQueued = false;
      await refreshRechargeDrawer();
    });
  }

  function observeNode(node) {
    if (!node || observedNodes.has(node)) return;

    const observer = new MutationObserver(queueRefresh);
    observer.observe(node, { childList: true, subtree: true });
    observedNodes.add(node);
  }

  function observeRechargeDrawer() {
    observeNode(document.querySelector(RECHARGE_ROOT_SELECTOR) || document.body);
    collectShadowRoots().forEach(observeNode);
  }

  function bindCartEvents() {
    document.addEventListener('cart:updated', queueRefresh);
    document.addEventListener('shopify:cart:updated', queueRefresh);

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, queueRefresh);
    }
  }

  function init() {
    bindCartEvents();
    observeRechargeDrawer();
    queueRefresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
